import { useMemo, useState } from "react";
import {
  Badge,
  Card,
  Divider,
  Group,
  NumberFormatter,
  SegmentedControl,
  Stack,
  Text,
  NativeSelect,
  Progress,
  Tooltip,
} from "@mantine/core";
import { CalendarDays, Clock, UserRound, ListOrdered } from "lucide-react";
import { Appointment } from "../interfaces/services/Apoointment";
import { parseFixedISOStringToDate } from "../utils/dateUtils";

type Props = {
  appointments: Appointment[];
  loading?: boolean;
};

const statusLabel: Record<string, string> = {
  Approved: "Onaylandı",
  Pending: "Bekliyor",
  Completed: "Tamamlandı",
  Canceled: "İptal",
  NoShow: "Gelmedi",
};

const statusColor: Record<string, string> = {
  Approved: "blue",
  Pending: "yellow",
  Completed: "green",
  Canceled: "red",
  NoShow: "gray",
};

function getStatusBadge(status: string) {
  const color = statusColor[status] ?? "blue";
  const label = statusLabel[status] ?? status;
  return (
    <Badge color={color} variant="light">
      {label}
    </Badge>
  );
}

function fmtDayHeader(d?: Date | null) {
  if (!d) return "—";
  const today = new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const delta = Math.round((d0.getTime() - t0.getTime()) / (24 * 3600 * 1000));
  if (delta === 0) return "Bugün";
  if (delta === 1) return "Yarın";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    weekday: "long",
  });
}

function withinFuture(a: Appointment) {
  const end = parseFixedISOStringToDate(a.appointmentTime.appointmentEndDate);
  if (!end) return false;
  return end.getTime() >= Date.now();
}

function withinPast(a: Appointment) {
  const end = parseFixedISOStringToDate(a.appointmentTime.appointmentEndDate);
  if (!end) return false;
  return end.getTime() < Date.now();
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export default function CustomerAppointments({
  appointments,
  loading = false,
}: Props) {
  const [scope, setScope] = useState<"upcoming" | "past" | "all">("upcoming");
  const [status, setStatus] = useState<string>("all");

  // filtreleme
  const filtered = useMemo(() => {
    let list = [...appointments];

    if (scope === "upcoming") list = list.filter(withinFuture);
    if (scope === "past") list = list.filter(withinPast);

    if (status !== "all")
      list = list.filter(
        (a) => (a.status ?? "").toLowerCase() === status.toLowerCase(),
      );

    list.sort((a, b) => {
      const at =
        parseFixedISOStringToDate(
          a.appointmentTime.appointmentStartDate,
        )?.getTime() ?? 0;
      const bt =
        parseFixedISOStringToDate(
          b.appointmentTime.appointmentStartDate,
        )?.getTime() ?? 0;
      if (scope === "past") return bt - at;
      return at - bt;
    });

    return list;
  }, [appointments, status, scope]);

  // tarihe göre gruplama
  const groups = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of filtered) {
      const sd =
        parseFixedISOStringToDate(a.appointmentTime.appointmentStartDate) ??
        new Date();
      const key = new Date(
        sd.getFullYear(),
        sd.getMonth(),
        sd.getDate(),
      ).toISOString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    // key'leri tarihe göre sırala
    const entries = [...map.entries()].sort((e1, e2) => {
      const t1 = new Date(e1[0]).getTime();
      const t2 = new Date(e2[0]).getTime();
      if (scope === "past") return t2 - t1;
      return t1 - t2;
    });
    return entries;
  }, [filtered, scope]);

  const renderSessionInfo = (a: Appointment) => {
    const isSession = a.serviceSnapshot?.type === "Session";
    if (!isSession) return null;

    const current = a.sessionNumber ?? 0;
    const total = a.serviceSnapshot?.sessionCount ?? 0;
    const intervalDays = a.serviceSnapshot?.sessionIntervalDays ?? 0;

    const start = parseFixedISOStringToDate(
      a.appointmentTime.appointmentStartDate,
    );

    // En erken bir sonraki alınabilir tarih: randevu başlangıcı + intervalDays
    const nextEligible =
      start && intervalDays > 0
        ? new Date(start.getTime() + intervalDays * MS_PER_DAY)
        : undefined;

    const pct =
      total > 0 && current > 0
        ? Math.max(0, Math.min(100, Math.round((current / total) * 100)))
        : 0;

    const remaining =
      total > 0 && current > 0 ? Math.max(0, total - current) : undefined;

    return (
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap">
          <Badge
            variant="light"
            color="indigo"
            leftSection={<ListOrdered size={14} />}
          >
            Seans {current > 0 ? current : "—"}
            {total > 0 ? ` / ${total}` : ""}
          </Badge>

          {typeof intervalDays === "number" && intervalDays > 0 && (
            <Badge variant="outline" color="teal">
              Aralık: {intervalDays} gün
            </Badge>
          )}
        </Group>

        {total > 0 && (
          <>
            <Progress value={pct} size="sm" />
            <Group justify="space-between">
              {nextEligible && current < total && (
                <Tooltip
                  label="Bu tarihten sonra bir sonraki seansı planlayabilirsiniz."
                  withArrow
                >
                  <Text size="xs" c="dimmed">
                    En erken sonraki seans:{" "}
                    {nextEligible.toLocaleString("tr-TR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Tooltip>
              )}
            </Group>
          </>
        )}
      </Stack>
    );
  };

  const renderCard = (a: Appointment) => {
    const start = parseFixedISOStringToDate(
      a.appointmentTime.appointmentStartDate,
    );
    const end = parseFixedISOStringToDate(a.appointmentTime.appointmentEndDate);
    const timeLabel =
      start && end
        ? `${start.toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
          })} – ${end.toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        : "-";

    const employeeName =
      `${a.employeeSnapshot?.name ?? ""} ${
        a.employeeSnapshot?.lastname ?? ""
      }`.trim() || "—";
    return (
      <Card withBorder radius="lg" shadow="sm" key={a.code}>
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              {getStatusBadge(a.status)}
              <Text fw={600} size="lg">
                {a.serviceSnapshot?.name ?? "Hizmet"}
              </Text>
              <Text fw={600} size="lg">
                {fmtDayHeader(start)}
              </Text>
            </Group>
            <Group gap="xs">
              <Clock size={18} />
              <Text fw={600}>{timeLabel}</Text>
            </Group>
          </Group>

          <Group gap="md">
            <Badge variant="outline" leftSection={<UserRound size={14} />}>
              {employeeName}
            </Badge>
            {typeof a.serviceSnapshot?.duration === "number" &&
              a.serviceSnapshot.duration > 0 && (
                <Badge variant="dot">{a.serviceSnapshot.duration} dk</Badge>
              )}
            {/*<Badge variant="light" color="grape">
              <NumberFormatter value={price} suffix=" ₺" thousandSeparator />
            </Badge>*/}
          </Group>

          {/* Seans Bilgisi (yalnızca Session tipinde) */}
          {renderSessionInfo(a)}

          {a.note && (
            <>
              <Divider />
              <Text size="sm" c="dimmed" lineClamp={2}>
                {a.note}
              </Text>
            </>
          )}
        </Stack>
      </Card>
    );
  };

  return (
    <>
      {/* Üst filtre çubuğu */}
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="sm">
          <SegmentedControl
            value={scope}
            onChange={(v) => setScope(v as any)}
            data={[
              { label: "Yaklaşan", value: "upcoming" },
              { label: "Geçmiş", value: "past" },
              { label: "Tümü", value: "all" },
            ]}
          />
          <NativeSelect
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value)}
            data={[
              { value: "all", label: "Tüm durumlar" },
              { value: "Approved", label: statusLabel.Approved },
              { value: "Pending", label: statusLabel.Pending },
              { value: "Completed", label: statusLabel.Completed },
              { value: "Canceled", label: statusLabel.Canceled },
              { value: "NoShow", label: statusLabel.NoShow },
            ]}
          />
        </Group>
      </Group>

      {/* İçerik */}
      {loading ? (
        <Stack gap="sm">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} withBorder radius="lg" mih={92} />
          ))}
        </Stack>
      ) : groups.length === 0 ? (
        <Card withBorder radius="lg" p="xl" ta="center">
          <Stack gap="xs" align="center">
            <CalendarDays size={28} />
            <Text fw={600}>Randevu bulunamadı</Text>
            <Text c="dimmed" size="sm">
              Filtreleri değiştirerek tekrar deneyin.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Stack gap="lg">
          {groups.map(([key, items]) => (
            <Stack key={key} gap="sm">
              <Text fw={700} size="sm" c="dimmed">
                {new Date(key).toLocaleDateString("tr-TR")}
              </Text>
              <Stack gap="sm">{items.map((a) => renderCard(a))}</Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
}
